// ACO Braintree payment integration
// Version date: 2026-03-24

interface BraintreePaymentPayload {
    nonce: string;
}

interface BraintreeDropinInstance {
    requestPaymentMethod(
        callback: (error: unknown, payload?: BraintreePaymentPayload) => void,
    ): void;
    clearSelectedPaymentMethod(): void;
}

interface BraintreeDropinApi {
    create(
        options: { authorization: string; container: string },
        callback: (error: unknown, instance?: BraintreeDropinInstance) => void,
    ): void;
}

interface RecaptchaEnterpriseApi {
    ready(callback: () => void): void;
    execute(key: string, options: { action: string }): Promise<string>;
}

declare const braintree: { dropin: BraintreeDropinApi };
declare const grecaptcha: { enterprise: RecaptchaEnterpriseApi };

// assume these IDs for dom objects
const containerid = "#dropin-container";
const submitbuttonid = "#submit-button";
const processingid = "#processing-message";
const checkoutmessageid = "#checkout-message";

export interface BraintreeConfig {
    clientToken: string;
    urlProcess: string;
    urlReceipt: string;
    recaptchaKey?: string; // optional, used for reCAPTCHA
}

export interface ProcessPaymentRequest {
    paymentMethodNonce: string;
    recaptchaToken?: string;
}

export interface RegistrationResult {
    success: boolean;
    message?: string | null;
    redirect: boolean;
}

export class BraintreePay {
    private submitting = false;
    config: BraintreeConfig;
    submitButton: HTMLInputElement;
    container: HTMLDivElement;
    processing: HTMLDivElement;
    checkoutmessage: HTMLDivElement;

    constructor(config: BraintreeConfig) {
        this.config = config;
        this.submitButton = this.getRequiredElement<HTMLInputElement>(submitbuttonid);
        this.container = this.getRequiredElement<HTMLDivElement>(containerid);
        this.processing = this.getRequiredElement<HTMLDivElement>(processingid);
        this.checkoutmessage = this.getRequiredElement<HTMLDivElement>(checkoutmessageid);
    }

    initialize(): void {
        const self = this;
        if (typeof braintree === "undefined") {
            this.showError("The payment form could not be loaded. Please reload the page or contact us.");
            this.submitButton.disabled = true;
            return;
        }
        braintree.dropin.create({
            // Insert your tokenization key here
            authorization: this.config.clientToken,
            container: "#dropin-container",
        }, function (createErr: unknown, instance?: BraintreeDropinInstance) {
            if (createErr) {
                // Handle any errors that might've occurred when creating Drop-in
                console.error(createErr);
                self.showError(self.getErrorMessage(createErr));
                self.submitButton.disabled = true;
                return;
            }
            if (!instance) {
                self.showError("Braintree did not return a payment form instance.");
                self.submitButton.disabled = true;
                return;
            }
            self.submitButton.addEventListener("click", function () {
                self.handleSubmit(instance);
            });
            self.submitButton.disabled = false;
        });
    }

    handleSubmit(instance: BraintreeDropinInstance): void {
        if (this.submitting) {
            return;
        }
        this.submitting = true;
        this.checkoutmessage.style.display = "none";
        const self = this;
        self.submitButton.disabled = true;
        self.container.style.display = "none";
        self.processing.style.display = "block";
        instance.requestPaymentMethod(
            function (requestPaymentMethodErr: unknown, payload?: BraintreePaymentPayload) {
                // When the user clicks on the 'Submit payment' button this code will send the
                // encrypted payment information in a variable called a payment method nonce
                if (requestPaymentMethodErr) {
                    console.error(requestPaymentMethodErr);
                    self.showError(self.getErrorMessage(requestPaymentMethodErr));
                    return;
                }
                if (!payload) {
                    self.showError("Braintree did not return a payment method.");
                    return;
                }
                void self.sendPaymentSuccess(instance, payload);
            },
        );
    }

    async getRecaptchaToken(timeout: number = 2000): Promise<string> {
        // wrap grecaptcha.enterprise.ready in a Promise
        const readyPromise = new Promise<void>((resolve) => {
            grecaptcha.enterprise.ready(() => resolve());
        });

        // create a timeout Promise that rejects after `timeout` ms
        const timeoutPromise = new Promise<never>((_, reject) => {
            const id = setTimeout(() => {
                clearTimeout(id);
                reject(new Error(`reCAPTCHA ready timed out after ${timeout} ms`));
            }, timeout);
        });

        // wait for whichever comes first
        await Promise.race([readyPromise, timeoutPromise]);

        // once ready, execute and return the token
        return grecaptcha.enterprise.execute(
            this.config.recaptchaKey!,
            { action: 'pay' }
        );
    }

    async sendPaymentSuccess(
        instance: BraintreeDropinInstance,
        payload: BraintreePaymentPayload,
    ): Promise<void> {
        try {
            let recaptchaToken: string;
            if (this.config.recaptchaKey) {
                recaptchaToken = await this.getRecaptchaToken();
            }
            else {
                recaptchaToken = ''; // No reCAPTCHA key provided, use empty string
            }
            const request: ProcessPaymentRequest = {
                paymentMethodNonce: payload.nonce,
                recaptchaToken: recaptchaToken || undefined,
            };
            const antiforgeryToken = this.getRequiredElement<HTMLInputElement>(
                '#dropin-wrapper input[name="__RequestVerificationToken"]',
            ).value;
            const response = await fetch(this.config.urlProcess, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "RequestVerificationToken": antiforgeryToken,
                },
                credentials: "same-origin",
                body: JSON.stringify(request),
            });

            let responseBody: unknown;
            try {
                responseBody = await response.json();
            } catch {
                throw new Error(`The server returned an invalid response (${response.status}).`);
            }

            if (!this.isRegistrationResult(responseBody)) {
                throw new Error("The server returned an invalid payment result.");
            }
            const result = responseBody;

            if (!response.ok) {
                throw new Error(result.message || `The payment request failed (${response.status}).`);
            }

            if (result.success) {
                if (!result.redirect) {
                    throw new Error("The server returned an inconsistent payment result.");
                }
                window.location.assign(this.config.urlReceipt);
                return;
            }

            throw new Error(result.message || "The payment could not be processed.");
        } catch (err: unknown) {
            console.error("Payment request failed:", err);
            instance.clearSelectedPaymentMethod();
            this.showError(`Payment Error: ${this.getErrorMessage(err)}`);
        }
    }

    showError(errormsg: string): void {
        this.submitting = false;
        this.submitButton.disabled = false;
        this.container.style.display = "block";
        this.processing.style.display = "none";
        if (errormsg) {
            this.checkoutmessage.style.display = "block";
            this.checkoutmessage.textContent = errormsg;
        }
    }

    private getRequiredElement<T extends HTMLElement>(selector: string): T {
        const element = document.querySelector<T>(selector);
        if (!element) {
            throw new Error(`Required payment element was not found: ${selector}`);
        }
        return element;
    }

    private getErrorMessage(error: unknown): string {
        return error instanceof Error && error.message
            ? error.message
            : String(error || "An unexpected error occurred.");
    }

    private isRegistrationResult(value: unknown): value is RegistrationResult {
        if (typeof value !== "object" || value === null) {
            return false;
        }
        const candidate = value as Partial<RegistrationResult>;
        return typeof candidate.success === "boolean"
            && typeof candidate.redirect === "boolean"
            && (candidate.message === undefined
                || candidate.message === null
                || typeof candidate.message === "string");
    }
}
